import {
  axios,
  fetch_entity_profiles,
  fetch_scoped_entity_types,
  get_request_config,
  hydrate_case_display_title,
  is_real_case_record,
  is_open_case_status,
  map_backend_case,
  normalize_key,
  read_session_current_user_id,
  to_number,
} from './company_cases_helpers';
import { GET_USERS } from '@/utils/system_constants/constant.js';
import { fetch_open_coordinator_assigned_company_cases } from './company_cases_live_queue';

function project_params(project_id) {
  return project_id ? { project_id } : {};
}

function empty_batch(entity_type, records = []) {
  return { entity_type, records };
}

async function fetch_profile_batch(entity_type, project_id) {
  const records = await safe_fetch_entity_profiles({
    entity_type_id: entity_type.id,
    page_size: 100,
    page: 1,
    ...project_params(project_id),
  });

  return empty_batch(entity_type, records);
}

async function safe_fetch_entity_profiles(params = {}) {
  try {
    return await fetch_entity_profiles(params);
  } catch {
    return [];
  }
}

async function fetch_profile_batches(entity_types = [], project_id = null) {
  if (entity_types.length) return Promise.all(
    entity_types.map((type) => fetch_profile_batch(type, project_id))
  );

  const records = await safe_fetch_entity_profiles({
    page_size: 100,
    page: 1,
    ...project_params(project_id),
  });
  return [{ entity_type: {}, records }];
}

function cases_error_message(error) {
  return error?.response?.data?.message || error?.message || 'Failed to load cases.';
}

function assignment_user_ids(case_item = {}) {
  return (case_item?.assignment_user_ids || case_item?.application_assigned_users || [])
    .map((assignment) => to_number(
      typeof assignment === 'object'
        ? assignment?.assigned_to_id
          || assignment?.assigned_user_id
          || assignment?.user_id
          || assignment?.user?.id
          || assignment?.assigned_to?.id
          || assignment?.assigned_to?.user_id
          || assignment?.profile?.user_id
        : assignment
    ))
    .filter(Boolean);
}

function is_coordinator_role(role = '') {
  return normalize_key(role).includes('coordinator');
}

function has_full_case_visibility(component) {
  const role_key = normalize_key(component.current_role);
  return component.can_route_cases
    || role_key === 'system-admin'
    || role_key === 'company-admin'
    || is_coordinator_role(role_key);
}

function should_limit_cases_to_assignments(component) {
  return !has_full_case_visibility(component);
}

function should_use_coordinator_assignment_queue(context = {}) {
  return is_coordinator_role(context.current_role);
}

function visible_cases_for_current_user(component, cases = []) {
  if (!should_limit_cases_to_assignments(component)) {
    return cases;
  }

  const current_user_id = read_session_current_user_id();
  if (!current_user_id) {
    return cases;
  }

  return cases.filter((case_item) => assignment_user_ids(case_item).includes(current_user_id));
}

function normalize_case_batches(profile_batches = [], users_by_id = new Map()) {
  return profile_batches
    .flatMap(({ records, entity_type }) => records
      .filter((record) => is_real_case_record(record, entity_type))
      .map((record) => map_backend_case(record, entity_type, users_by_id)))
    .filter((case_item) => is_open_case_status(case_item.status))
    .sort((left, right) => Number(right.sort_time || 0) - Number(left.sort_time || 0));
}

async function hydrate_case_titles(cases = []) {
  return Promise.all(cases.map((case_item) => hydrate_case_display_title(case_item)));
}

async function fetch_users_directory(project_id = null) {
  try {
    const response = await axios.get(GET_USERS, get_request_config({
      params: {
        page: 1,
        page_size: 500,
        ...(project_id ? { project_id } : {}),
      },
    }));
    const payload = response?.data?.data;
    const rows = Array.isArray(payload)
      ? payload
      : Array.isArray(response?.data)
        ? response.data
        : [];

    return new Map(
      rows
        .map((row) => {
          const user_id = to_number(row?.user_id || row?.id || row?.user?.id);
          return user_id ? [user_id, row?.user || row] : null;
        })
        .filter(Boolean)
    );
  } catch {
    return new Map();
  }
}

export async function load_visible_open_company_cases({
  project_id = null,
  current_role = '',
  can_route_cases = false,
} = {}) {
  if (should_use_coordinator_assignment_queue({ current_role })) {
    return fetch_open_coordinator_assigned_company_cases(project_id);
  }

  const { entity_types } = await fetch_scoped_entity_types(project_id);

  const [profile_batches, users_by_id] = await Promise.all([
    fetch_profile_batches(entity_types, project_id),
    fetch_users_directory(project_id),
  ]);
  const base_cases = normalize_case_batches(profile_batches, users_by_id);
  const hydrated_cases = await hydrate_case_titles(base_cases);
  return visible_cases_for_current_user(
    { current_role, can_route_cases },
    hydrated_cases
  );
}

export const company_cases_loading_methods = {
  async fetch_cases() {
    const { entity_types, coordinator_scope } = await fetch_scoped_entity_types(this.project_id);
    if (should_use_coordinator_assignment_queue(this)) {
      return { entity_types, profile_batches: [], coordinator_scope };
    }

    const profile_batches = await fetch_profile_batches(entity_types, this.project_id);
    return { entity_types, profile_batches, coordinator_scope };
  },
  normalize_cases(profile_batches = []) {
    return normalize_case_batches(profile_batches, this.case_owner_directory_by_id);
  },
  update_case_types(entity_types = []) {
    this.case_types = entity_types;
    if (!this.new_case_form.entity_type_id && entity_types[0]?.id) {
      this.new_case_form.entity_type_id = String(entity_types[0].id);
    }
  },
  async update_cases(profile_batches = []) {
    if (should_use_coordinator_assignment_queue(this)) {
      this.cases = await fetch_open_coordinator_assigned_company_cases(this.project_id);
      return;
    }

    const base_cases = this.normalize_cases(profile_batches);
    const hydrated_cases = await hydrate_case_titles(base_cases);
    const current_role_key = normalize_key(this.current_role);
    this.cases = current_role_key === 'system-admin'
      ? hydrated_cases
      : visible_cases_for_current_user(this, hydrated_cases);
  },
  async load_company_cases() {
    this.loading_cases = true;
    this.cases_error = '';
    try {
      const use_coordinator_queue = should_use_coordinator_assignment_queue(this);
      const [{ entity_types, profile_batches }, users_by_id] = await Promise.all([
        this.fetch_cases(),
        use_coordinator_queue
          ? Promise.resolve(new Map())
          : fetch_users_directory(this.project_id),
      ]);
      this.case_owner_directory_by_id = users_by_id;
      this.update_case_types(entity_types);
      await this.update_cases(profile_batches);
      await this.load_workflow_status_filter_options();
    } catch (error) {
      this.cases = [];
      this.case_owner_directory_by_id = new Map();
      this.workflow_status_filter_options = [];
      this.cases_error = cases_error_message(error);
    } finally {
      this.loading_cases = false;
    }
  },
};
